import { useState, useEffect, useCallback } from 'react';
import { ANIMATION_CONFIG } from '@/constants';
import type { AvatarState } from '@/types';

export const useAvatarAnimation = (isSpeaking: boolean) => {
  const [avatarState, setAvatarState] = useState<AvatarState>({
    mouthOpenness: 0,
    expression: 'neutral',
    eyeBlinkProgress: 0,
  });

  // Mouth animation for speaking
  useEffect(() => {
    if (!isSpeaking) {
      setAvatarState((prev) => ({ ...prev, mouthOpenness: 0, expression: 'neutral' }));
      return;
    }

    setAvatarState((prev) => ({ ...prev, expression: 'speaking' }));

    let animationFrame: number;
    let lastTime = performance.now();
    let isOpening = true;

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;
      const duration = isOpening
        ? ANIMATION_CONFIG.MOUTH_OPEN_DURATION
        : ANIMATION_CONFIG.MOUTH_CLOSE_DURATION;

      if (deltaTime >= duration) {
        isOpening = !isOpening;
        lastTime = currentTime;

        setAvatarState((prev) => ({
          ...prev,
          mouthOpenness: isOpening ? Math.random() * 0.4 + 0.6 : Math.random() * 0.3,
        }));
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [isSpeaking]);

  // Eye blink animation
  useEffect(() => {
    const scheduleBlink = () => {
      const delay =
        Math.random() *
          (ANIMATION_CONFIG.BLINK_INTERVAL_MAX - ANIMATION_CONFIG.BLINK_INTERVAL_MIN) +
        ANIMATION_CONFIG.BLINK_INTERVAL_MIN;

      return setTimeout(() => {
        performBlink();
        blinkTimeout = scheduleBlink();
      }, delay);
    };

    const performBlink = () => {
      let progress = 0;
      const startTime = performance.now();

      const blinkAnimation = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        progress = Math.min(elapsed / ANIMATION_CONFIG.BLINK_DURATION, 1);

        // Sine wave for smooth blink
        const blinkValue = Math.sin(progress * Math.PI);
        setAvatarState((prev) => ({ ...prev, eyeBlinkProgress: blinkValue }));

        if (progress < 1) {
          requestAnimationFrame(blinkAnimation);
        } else {
          setAvatarState((prev) => ({ ...prev, eyeBlinkProgress: 0 }));
        }
      };

      requestAnimationFrame(blinkAnimation);
    };

    let blinkTimeout = scheduleBlink();

    return () => {
      clearTimeout(blinkTimeout);
    };
  }, []);

  const setExpression = useCallback((expression: AvatarState['expression']) => {
    setAvatarState((prev) => ({ ...prev, expression }));
  }, []);

  return { avatarState, setExpression };
};
